import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiRefreshCw, FiSearch, FiX } from 'react-icons/fi';
import { useBans } from '../hooks/useBans';
import TempBanModal from '../components/TempBanModal';
import '../styles/bans.scss';

const TABS = [
    { key: 'banned', label: 'الموقوفون حالياً' },
    { key: 'nearBan', label: 'على وشك الإيقاف' },
    { key: 'userCancels', label: 'إلغاءات المستخدمين' },
    { key: 'alerts', label: 'قائمة الإشعارات' },
];

const formatDateTime = (value) => (value ? new Date(value).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const formatRemaining = (ms) => {
    if (ms <= 0) return 'انتهى';
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    if (d > 0) return `${d} يوم ${h} ساعة`;
    if (h > 0) return `${h} ساعة ${m} دقيقة`;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const DriverCell = ({ d }) => (
    <div className="driver-cell">
        {d.carImage ? <img src={d.carImage} alt="" /> : <div className="avatar">{(d.driverName || '?').slice(0, 1)}</div>}
        <div>
            <div className="name">{d.driverName} {d.driverNumber ? <span className="num">#{d.driverNumber}</span> : null}</div>
            <div className="muted num">{d.phoneNumber}</div>
        </div>
    </div>
);

// One cancellation log (driver- or user-cancelled) with per-entry removal.
const LogList = ({ title, entries, kind, onRemove, showBy }) => (
    <div className="log-list">
        <h4>{title}</h4>
        {entries.length === 0 ? <p className="muted">لا توجد سجلات</p> : entries.map((e) => (
            <div key={`${e.tripNumber}-${e.date}`} className="log-row">
                <span className="num">رحلة #{e.tripNumber}</span>
                <span className="muted num">{formatDateTime(e.date)}</span>
                {showBy ? <span className={`by-chip ${e.by}`}>{e.by === 'shopOwner' ? 'ورشة' : 'مستخدم'}</span> : null}
                <button type="button" className="icon-btn" title="حذف من السجل (لا يحذف الرحلة)" onClick={() => onRemove(kind, e)}>
                    <FiX />
                </button>
            </div>
        ))}
    </div>
);

const BanHistory = ({ history }) => (
    <div className="log-list">
        <h4>سجل الإيقافات</h4>
        {history.length === 0 ? <p className="muted">لم يتم إيقافه من قبل</p> : history.map((h, i) => (
            <div key={i} className="log-row history">
                <span className="num">{formatDateTime(h.from)} ← {formatDateTime(h.until)}</span>
                <span className="muted">{h.by === 'system' ? 'النظام' : h.adminName || 'الإدارة'}</span>
                <span className="reason">{h.reason}</span>
                {h.liftedAt ? <span className="by-chip lifted">رُفع{h.liftedBy ? ` بواسطة ${h.liftedBy}` : ''}</span> : null}
            </div>
        ))}
    </div>
);

const DriverRow = ({ d, tab, config, now, open, onToggle, onBan, onExtend, onLift, onRemove }) => {
    const remaining = d.tempBanUntil ? new Date(d.tempBanUntil).getTime() - now : 0;
    return (
        <div className={`ban-row ${open ? 'open' : ''} ${tab === 'nearBan' && d.oneAway ? 'warn' : ''}`}>
            <div className="ban-row__main" onClick={onToggle}>
                <DriverCell d={d} />
                {tab === 'banned' ? (
                    <>
                        <div className="stat">
                            <span className="label">متبقي</span>
                            <span className="value num">{formatRemaining(remaining)}</span>
                        </div>
                        <div className="stat">
                            <span className="label">حتى</span>
                            <span className="value num">{formatDateTime(d.tempBanUntil)}</span>
                        </div>
                        <div className="stat wide">
                            <span className="label">{d.tempBanBy === 'system' ? 'إيقاف تلقائي' : `بواسطة ${d.tempBanAdmin || 'الإدارة'}`}</span>
                            <span className="value reason">{d.tempBanReason || '—'}</span>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="stat">
                            <span className="label">ألغاها السائق</span>
                            <span className={`value num ${d.oneAway ? 'danger' : ''}`}>{d.driverCancellations} / {config.maxMonthlyCancellations}</span>
                        </div>
                        <div className="stat">
                            <span className="label">ألغاها المستخدمون</span>
                            <span className={`value num ${config.userCancellationThreshold && d.userCancellations >= config.userCancellationThreshold ? 'danger' : ''}`}>
                                {d.userCancellations}{config.userCancellationThreshold ? ` / ${config.userCancellationThreshold}` : ''}
                            </span>
                        </div>
                        <div className="stat">
                            <span className="label">رحلات مكتملة لمسح السجل</span>
                            <span className="value num">
                                {config.forgiveAfterCompletedTrips ? `${d.completedSinceFirstCancellation} / ${config.forgiveAfterCompletedTrips}` : 'متوقف'}
                            </span>
                        </div>
                    </>
                )}
                <div className="row-actions" onClick={(e) => e.stopPropagation()}>
                    {d.banned ? (
                        <>
                            <button type="button" className="btn ghost small" onClick={onExtend}>تعديل المدة</button>
                            <button type="button" className="btn success small" onClick={onLift}>رفع الإيقاف</button>
                        </>
                    ) : (
                        <button type="button" className="btn danger small" onClick={onBan}>إيقاف مؤقت</button>
                    )}
                </div>
                <FiChevronDown className="chevron" />
            </div>
            {open ? (
                <div className="ban-row__details">
                    <LogList title="رحلات ألغاها السائق (آخر 30 يوم)" entries={d.driverLog} kind="driver" onRemove={onRemove} />
                    <LogList title="رحلات ألغاها المستخدمون (آخر 30 يوم)" entries={d.userLog} kind="user" onRemove={onRemove} showBy />
                    <BanHistory history={d.banHistory || []} />
                </div>
            ) : null}
        </div>
    );
};

// Drivers who get a push when a driver is banned, is one cancellation away, or reaches the
// user-cancellation limit.
const AlertList = ({ drivers, onSave, search }) => {
    const [list, setList] = useState(drivers);
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    useEffect(() => setList(drivers), [drivers]);

    const ids = new Set(list.map((d) => String(d._id)));
    const dirty = list.length !== drivers.length || list.some((d) => !drivers.some((x) => String(x._id) === String(d._id)));

    const runSearch = async (e) => {
        e?.preventDefault();
        if (!q.trim()) return;
        setSearching(true);
        setResults(await search(q.trim()));
        setSearching(false);
    };

    return (
        <div className="alert-list">
            <p className="muted">يصل إشعار لهؤلاء السائقين عند إيقاف سائق، أو عندما يصبح سائق على بعد إلغاء واحد من الإيقاف، أو عندما تصل إلغاءات المستخدمين لسائق إلى الحد المحدد.</p>
            <form className="search-row" onSubmit={runSearch}>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو رقم الهاتف أو رقم السائق" />
                <button type="submit" className="btn primary" disabled={searching}><FiSearch /> {searching ? 'جارٍ البحث...' : 'بحث'}</button>
            </form>
            {results.length ? (
                <div className="search-results">
                    {results.map((d) => (
                        <div key={d._id} className="alert-row">
                            <DriverCell d={d} />
                            {ids.has(String(d._id)) ? (
                                <span className="muted">في القائمة</span>
                            ) : (
                                <button type="button" className="btn ghost small" onClick={() => setList((prev) => [...prev, d])}>إضافة</button>
                            )}
                        </div>
                    ))}
                </div>
            ) : null}

            <h4>السائقون في القائمة ({list.length})</h4>
            {list.length === 0 ? <p className="muted">لا يوجد سائقون في القائمة</p> : list.map((d) => (
                <div key={d._id} className="alert-row">
                    <DriverCell d={d} />
                    {d.hasPushToken === false ? <span className="by-chip warn">لا يستقبل إشعارات</span> : null}
                    <button type="button" className="icon-btn" title="إزالة" onClick={() => setList((prev) => prev.filter((x) => String(x._id) !== String(d._id)))}>
                        <FiX />
                    </button>
                </div>
            ))}
            <div className="modal-actions">
                <button type="button" className="btn ghost" disabled={!dirty || saving} onClick={() => setList(drivers)}>تراجع</button>
                <button
                    type="button"
                    className="btn primary"
                    disabled={!dirty || saving}
                    onClick={async () => { setSaving(true); await onSave(list.map((d) => d._id)); setSaving(false); }}
                >
                    {saving ? 'جارٍ الحفظ...' : 'حفظ القائمة'}
                </button>
            </div>
        </div>
    );
};

export const Bans = () => {
    const user = useSelector((state) => state.userController.user);
    const navigate = useNavigate();
    const { data, loading, load, banDriver, liftBan, removeLogEntry, saveAlertDrivers, searchDrivers } = useBans();
    const [tab, setTab] = useState('banned');
    const [openId, setOpenId] = useState(null);
    const [banTarget, setBanTarget] = useState(null); // { driver, extend }
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    // Live countdowns; reload when a ban runs out.
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const nextExpiry = useMemo(() => Math.min(...(data?.banned || []).map((d) => new Date(d.tempBanUntil).getTime()), Infinity), [data]);
    useEffect(() => {
        if (Number.isFinite(nextExpiry) && now > nextExpiry + 1500) load();
    }, [now, nextExpiry, load]);

    if (!user) return null;
    const config = data?.config || {};
    const counts = {
        banned: data?.banned?.length || 0,
        nearBan: data?.nearBan?.length || 0,
        userCancels: data?.userCancels?.length || 0,
        alerts: data?.alertDrivers?.length || 0,
    };
    const rows = tab === 'alerts' ? [] : (data?.[tab] || []);

    const confirmLift = (d) => {
        if (window.confirm(`رفع الإيقاف عن ${d.driverName}؟ سيتمكن من استقبال الطلبات فوراً.`)) liftBan(d._id);
    };
    const confirmRemove = (d) => (kind, entry) => {
        if (window.confirm(`حذف الرحلة #${entry.tripNumber} من سجل ${d.driverName}؟ الرحلة نفسها تبقى في صفحة الرحلات.`)) {
            removeLogEntry(d._id, kind, entry);
        }
    };

    return (
        <section className="container bans-page">
            <header className="bans-header">
                <div>
                    <h2>حالات الباند</h2>
                    {data ? (
                        <p className="muted">
                            {config.enabled ? `الإيقاف التلقائي: أكثر من ${config.maxMonthlyCancellations} إلغاءات خلال 30 يوماً ← إيقاف ${config.banDurationDays} أيام` : 'الإيقاف التلقائي متوقف'}
                            {config.forgiveAfterCompletedTrips ? ` · يُمسح السجل بعد ${config.forgiveAfterCompletedTrips} رحلة مكتملة` : ''}
                        </p>
                    ) : null}
                </div>
                <button type="button" className="btn ghost" onClick={load} disabled={loading}>
                    <FiRefreshCw className={loading ? 'spin' : ''} /> تحديث
                </button>
            </header>

            <div className="bans-tabs">
                {TABS.map((t) => (
                    <button key={t.key} type="button" className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => { setTab(t.key); setOpenId(null); }}>
                        {t.label}
                        <span className="count num">{counts[t.key]}</span>
                    </button>
                ))}
            </div>

            {!data ? (
                <div className="placeholder">{loading ? 'جارٍ التحميل...' : 'تعذر تحميل البيانات'}</div>
            ) : tab === 'alerts' ? (
                <AlertList drivers={data.alertDrivers || []} onSave={saveAlertDrivers} search={searchDrivers} />
            ) : rows.length === 0 ? (
                <div className="placeholder">
                    {tab === 'banned' ? 'لا يوجد سائقون موقوفون حالياً'
                        : tab === 'nearBan' ? 'لا يوجد سائقون ألغوا رحلات خلال آخر 30 يوماً'
                            : config.userCancellationThreshold ? `لا يوجد سائقون وصلت إلغاءات المستخدمين لهم إلى ${config.userCancellationThreshold}` : 'حد إلغاءات المستخدمين متوقف من الإعدادات'}
                </div>
            ) : (
                <div className="ban-list">
                    {rows.map((d) => (
                        <DriverRow
                            key={d._id}
                            d={d}
                            tab={tab}
                            config={config}
                            now={now}
                            open={openId === d._id}
                            onToggle={() => setOpenId((prev) => (prev === d._id ? null : d._id))}
                            onBan={() => setBanTarget({ driver: d, extend: false })}
                            onExtend={() => setBanTarget({ driver: d, extend: true })}
                            onLift={() => confirmLift(d)}
                            onRemove={confirmRemove(d)}
                        />
                    ))}
                </div>
            )}

            {banTarget ? (
                <TempBanModal
                    driver={banTarget.driver}
                    extend={banTarget.extend}
                    onClose={() => setBanTarget(null)}
                    onSubmit={(payload) => banDriver(banTarget.driver._id, payload)}
                />
            ) : null}
        </section>
    );
};
